import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import * as THREE from "three";

interface TerrainHandle {
  destroy: () => void;
}

interface TrafficParticle {
  mesh: THREE.Mesh;
  curve: THREE.QuadraticBezierCurve3;
  progress: number;
}

interface City {
  lat: number;
  lon: number;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function latLonToVector3(lat: number, lon: number, radius: number): THREE.Vector3 {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const z = radius * Math.sin(phi) * Math.sin(theta);
  const y = radius * Math.cos(phi);
  return new THREE.Vector3(x, y, z);
}

export function initTerrain(container: HTMLElement): TerrainHandle {
  const reduced = prefersReducedMotion();
  gsap.registerPlugin(ScrollTrigger);

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0x050a14, 0.02);

  const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 100);
  camera.position.set(0, 0, 15);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  container.appendChild(renderer.domElement);

  const universeGroup = new THREE.Group();
  const globeGroup = new THREE.Group();
  universeGroup.add(globeGroup);
  scene.add(universeGroup);

  const starsGeometry = new THREE.BufferGeometry();
  const starsCount = 1500;
  const posArray = new Float32Array(starsCount * 3);
  for (let i = 0; i < starsCount * 3; i += 1) {
    posArray[i] = (Math.random() - 0.5) * 80;
  }
  starsGeometry.setAttribute("position", new THREE.BufferAttribute(posArray, 3));
  const starsMaterial = new THREE.PointsMaterial({
    size: 0.05,
    color: 0xffffff,
    transparent: true,
    opacity: 0.8
  });
  const starMesh = new THREE.Points(starsGeometry, starsMaterial);
  universeGroup.add(starMesh);

  const textureLoader = new THREE.TextureLoader();
  textureLoader.crossOrigin = "anonymous";
  const textureBase = "https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/planets";
  const earthTexture = textureLoader.load(`${textureBase}/earth_atmos_2048.jpg`);
  const specTexture = textureLoader.load(`${textureBase}/earth_specular_2048.jpg`);
  const normalTexture = textureLoader.load(`${textureBase}/earth_normal_2048.jpg`);
  const lightsTexture = textureLoader.load(`${textureBase}/earth_lights_2048.png`);
  const cloudsTexture = textureLoader.load(`${textureBase}/earth_clouds_1024.png`);

  const geometry = new THREE.SphereGeometry(5, 64, 64);
  const material = new THREE.MeshPhongMaterial({
    map: earthTexture,
    specularMap: specTexture,
    normalMap: normalTexture,
    emissiveMap: lightsTexture,
    emissive: 0xffcc33,
    emissiveIntensity: 0.6,
    color: 0x152030,
    specular: 0x555555,
    shininess: 10
  });
  const globe = new THREE.Mesh(geometry, material);
  globeGroup.add(globe);

  const cloudGeo = new THREE.SphereGeometry(5.08, 64, 64);
  const cloudMat = new THREE.MeshLambertMaterial({
    map: cloudsTexture,
    transparent: true,
    opacity: 0.4,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide
  });
  const cloudMesh = new THREE.Mesh(cloudGeo, cloudMat);
  globeGroup.add(cloudMesh);

  const atmosphereGeo = new THREE.SphereGeometry(5.2, 64, 64);
  const atmosphereMat = new THREE.ShaderMaterial({
    vertexShader: `
      varying vec3 vNormal;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec3 vNormal;
      void main() {
        float intensity = pow(0.6 - dot(vNormal, vec3(0, 0, 1.0)), 3.0);
        gl_FragColor = vec4(0.96, 0.62, 0.04, 1.0) * intensity * 1.5;
      }
    `,
    blending: THREE.AdditiveBlending,
    side: THREE.BackSide,
    transparent: true
  });
  const atmosphere = new THREE.Mesh(atmosphereGeo, atmosphereMat);
  globeGroup.add(atmosphere);

  const connectionsGroup = new THREE.Group();
  globeGroup.add(connectionsGroup);
  const trafficParticles: TrafficParticle[] = [];

  function createArc(startLat: number, startLon: number, endLat: number, endLon: number): void {
    const start = latLonToVector3(startLat, startLon, 5);
    const end = latLonToVector3(endLat, endLon, 5);
    const dist = start.distanceTo(end);
    const mid = start
      .clone()
      .add(end)
      .multiplyScalar(0.5)
      .normalize()
      .multiplyScalar(5 + dist * 0.2);

    const curve = new THREE.QuadraticBezierCurve3(start, mid, end);
    const points = curve.getPoints(50);
    const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.1
    });
    const line = new THREE.Line(lineGeometry, lineMaterial);
    connectionsGroup.add(line);

    const particleGeo = new THREE.SphereGeometry(0.06, 8, 8);
    const particleMat = new THREE.MeshBasicMaterial({ color: 0xf59e0b });
    const particle = new THREE.Mesh(particleGeo, particleMat);
    connectionsGroup.add(particle);

    trafficParticles.push({
      mesh: particle,
      curve,
      progress: Math.random()
    });
  }

  const cities: City[] = [
    { lat: 40.71, lon: -74.0 },
    { lat: 51.5, lon: -0.12 },
    { lat: 35.68, lon: 139.69 },
    { lat: -33.86, lon: 151.2 },
    { lat: -22.9, lon: -43.17 },
    { lat: 25.2, lon: 55.27 },
    { lat: 1.35, lon: 103.81 },
    { lat: 48.85, lon: 2.35 },
    { lat: 37.77, lon: -122.41 },
    { lat: 19.07, lon: 72.87 }
  ];

  for (let i = 0; i < cities.length; i += 1) {
    const source = cities[i];
    if (source === undefined) {
      continue;
    }
    for (let k = 0; k < 2; k += 1) {
      const targetIndex = (i + Math.floor(Math.random() * (cities.length - 1)) + 1) % cities.length;
      const target = cities[targetIndex];
      if (target !== undefined) {
        createArc(source.lat, source.lon, target.lat, target.lon);
      }
    }
  }

  cities.forEach((city) => {
    const pos = latLonToVector3(city.lat, city.lon, 5.01);
    const markerGeo = new THREE.RingGeometry(0.06, 0.08, 32);
    const markerMat = new THREE.MeshBasicMaterial({ color: 0xf59e0b, side: THREE.DoubleSide });
    const marker = new THREE.Mesh(markerGeo, markerMat);
    marker.position.copy(pos);
    marker.lookAt(0, 0, 0);
    globeGroup.add(marker);
  });

  const ambientLight = new THREE.AmbientLight(0x050505);
  scene.add(ambientLight);

  const sunLight = new THREE.DirectionalLight(0xffffff, 1.2);
  sunLight.position.set(10, 5, 10);
  scene.add(sunLight);

  const rimLight = new THREE.SpotLight(0xf59e0b, 8);
  rimLight.position.set(-10, 10, -5);
  rimLight.lookAt(0, 0, 0);
  scene.add(rimLight);

  let mouseX = 0;
  let mouseY = 0;
  let animationFrame = 0;

  const onMouseMove = (event: MouseEvent): void => {
    mouseX = (event.clientX / window.innerWidth) * 2 - 1;
    mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
  };

  const onResize = (): void => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  };

  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("resize", onResize);

  globeGroup.rotation.x = 0.2;
  globeGroup.rotation.y = 4.5;

  const timeline = reduced
    ? null
    : gsap.timeline({
        scrollTrigger: {
          trigger: "body",
          start: "top top",
          end: "bottom bottom",
          scrub: 1
        }
      });

  timeline
    ?.to(globeGroup.position, { x: 3.5, z: -2, duration: 5 }, "phase1")
    .to(globeGroup.rotation, { y: "+=1.5", x: 0.4, duration: 5 }, "phase1")
    .to(globeGroup.position, { x: -3.5, y: 0, z: -1, duration: 5 }, "phase2")
    .to(globeGroup.rotation, { y: "+=1.2", x: -0.2, duration: 5 }, "phase2")
    .to(globeGroup.position, { x: 0, y: -5, z: 2, duration: 5 }, "phase3")
    .to(globeGroup.rotation, { y: "+=1.0", x: 0.5, duration: 5 }, "phase3");

  if (reduced) {
    globeGroup.position.set(3.5, 0, -2);
  }

  function animate(): void {
    animationFrame = window.requestAnimationFrame(animate);

    if (!reduced) {
      globe.rotation.y += 0.0005;
      cloudMesh.rotation.y += 0.0007;
      cloudMesh.rotation.x += 0.0001;
      atmosphere.rotation.y += 0.0005;

      trafficParticles.forEach((particle) => {
        particle.progress += 0.004;
        if (particle.progress > 1) {
          particle.progress = 0;
        }
        particle.mesh.position.copy(particle.curve.getPoint(particle.progress));
      });

      starMesh.rotation.y -= 0.0002;

      gsap.to(universeGroup.rotation, {
        x: mouseY * 0.1,
        y: mouseX * 0.1,
        duration: 1,
        ease: "power2.out"
      });
    }

    renderer.render(scene, camera);
  }
  animate();

  return {
    destroy: () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", onResize);
      window.cancelAnimationFrame(animationFrame);
      timeline?.scrollTrigger?.kill();
      timeline?.kill();
      renderer.dispose();
      renderer.domElement.remove();
    }
  };
}
